import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import NetworkIndicator from '@/components/NetworkIndicator';
import SchoolLogo from '@/components/SchoolLogo';
import MainArea from './MainArea';
import { RootState } from '@/store';
import { useSceneFromURL } from '@/lib/useSceneFromURL';
import { useLayout } from '@/lib/useLayout';
import styles from './index.module.less';
import logger from '@/utils/logger';

export default function () {
  const room = useSelector((state: RootState) => state.room);
  const isJoined = room.isJoined;

  // 集成URL场景处理
  useSceneFromURL();

  // 使用统一的布局检测 hook（支持路径参数和查询参数）
  useLayout();

  // 读取 URL 主题参数并注入到 CSS 变量
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const match = url.pathname.match(/\/scene\/([^\/?#]+)/i);
      const sceneId = match ? decodeURIComponent(match[1]) : 'Custom';

      // 首先尝试恢复缓存的背景图片（优先命名空间，其次兼容旧键）
      const nsBg = sessionStorage.getItem(`theme:${sceneId}:bg`);
      const nsHero = sessionStorage.getItem(`theme:${sceneId}:hero`);
      const legacyBg = sessionStorage.getItem('themeBackgroundImage');
      const legacyHero = sessionStorage.getItem('themeHeroCSS');
      const cachedBackgroundImage = nsBg || legacyBg;
      const cachedThemeHeroCSS = nsHero || legacyHero;

      if (cachedThemeHeroCSS) {
        document.documentElement.style.setProperty('--theme-hero', cachedThemeHeroCSS);
        if (cachedBackgroundImage) {
          logger.info('[MainPage] 已恢复缓存的背景图片:', cachedBackgroundImage);
        }
      }

      // 布局检测已由 useLayout hook 处理

      const theme = url.searchParams.get('theme');
      if (!theme) return;
      // 简单内置几套示例主题；后续可从远端配置注入
      const presets: Record<
        string,
        {
          css: Record<string, string>;
        }
      > = {
        spring: {
          css: {
            '--theme-bg': 'linear-gradient(167.98deg, #f5fff8 0%, #e8fff4 100%)',
            '--theme-hero':
              'radial-gradient(ellipse at 20% 10%, #9fe8c9 0%, #75d0a3 60%, #86e3c2 100%), linear-gradient(135deg, #6fd2a0 0%, #8ad4b7 100%)',
            '--theme-bubble': 'linear-gradient(135deg, #7ccdf5, #63e6be)',
          },
        },
        festival: {
          css: {
            '--theme-bg': 'linear-gradient(167.98deg, #fff5f7 0%, #fff0f0 100%)',
            '--theme-hero':
              'radial-gradient(ellipse at 20% 10%, #ff9aa2 0%, #ff6f91 60%, #ff9671 100%), linear-gradient(135deg, #ff6f91 0%, #ff9671 100%)',
            '--theme-bubble': 'linear-gradient(135deg, #ff758c, #ff7eb3)',
          },
        },
        winter: {
          css: {
            '--theme-bg': 'linear-gradient(167.98deg, #f5f7ff 0%, #eef3ff 100%)',
            '--theme-hero':
              'radial-gradient(ellipse at 20% 10%, #a8c0ff 0%, #3f2b96 60%, #4e54c8 100%), linear-gradient(135deg, #3f2b96 0%, #4e54c8 100%)',
            '--theme-bubble': 'linear-gradient(135deg, #4294ff, #683dff)',
          },
        },
      };
      const preset = presets[theme];
      if (preset?.css) {
        Object.entries(preset.css).forEach(([key, val]) => {
          document.documentElement.style.setProperty(key, val);
        });
        // 将示例主题的 --theme-hero 同步写入按场景命名空间，交由 Provider 统一维护
        const heroCss = (preset.css as Record<string, string>)['--theme-hero'];
        if (heroCss && typeof heroCss === 'string') {
          try {
            sessionStorage.setItem(`theme:${sceneId}:hero`, heroCss);
            window.dispatchEvent(new CustomEvent('theme-config-updated'));
          } catch {}
        }
      }
    } catch {}
  }, [window.location.search]); // 监听URL参数变化，确保layout切换时重新执行

  return (
    <div className={styles.gradientBgContainer}>
      {/* 独立的校徽组件，始终在顶部 */}
      <SchoolLogo />
      {/* 直接渲染NetworkIndicator，无外层容器 */}
      {isJoined && <NetworkIndicator />}
      <MainArea />
    </div>
  );
}
